import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import {
  Coins, UploadSimple, DownloadSimple, MagnifyingGlass, ArrowsClockwise, X,
  CheckCircle, WarningCircle, ClockCounterClockwise, Gear, ArrowRight,
} from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/material-valuation`;
const POLL_INTERVAL_MS = 2000;
const DEFAULT_VALID_FROM = "2026-10-01"; // user's explicit default "01.10.2026"

const STATUS_STYLES = {
  "Ready": "text-[#027A48] bg-[#ECFDF3] border-[#ABEFC6]",
  "Existing Valuation / Review Required": "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]",
};
const STATUS_DEFAULT_STYLE = "text-[#B42318] bg-[#FEF3F2] border-[#FDA29B]";

const StatCard = ({ label, value, testId, tone }) => (
  <div className={`bg-white border rounded-md px-4 py-3 ${tone === "bad" ? "border-[#FDA29B]" : "border-[#D0D5DD]"}`} data-testid={testId}>
    <div className="text-xs text-[#667085] font-sans">{label}</div>
    <div className={`text-xl font-bold font-heading mt-0.5 ${tone === "bad" ? "text-[#B42318]" : "text-[#1D2939]"}`}>{value}</div>
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] font-bold border rounded px-1.5 py-0.5 whitespace-nowrap ${STATUS_STYLES[status] || STATUS_DEFAULT_STYLE}`}>
    {status}
  </span>
);

// Feb 2026, user's explicit ask - "SAP Material Valuation Activation"
// admin module. 3-step workflow: (1) upload an Item Code + Price Excel,
// (2) pick plant(s) + Valid From date, (3) validate every material x
// plant combination against live SAP Material Master + the centrally
// maintained ADG/Plant mapping, preview, then download the official
// SAP XML template (only "Ready" rows) for manual upload into SAP's own
// Mass Data Maintenance tool - this app never writes valuation directly.
export default function MaterialValuationActivationPage() {
  const [activeView, setActiveView] = useState("workflow");
  const [plants, setPlants] = useState([]);

  // Step 1: upload
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Step 2: plants + date
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [validFrom, setValidFrom] = useState(DEFAULT_VALID_FROM);

  // Step 3: validate + preview
  const [validating, setValidating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const validateTokenRef = useRef(0);

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Category Mapping
  const [mappings, setMappings] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [savingMapping, setSavingMapping] = useState(false);

  useEffect(() => {
    axios.get(`${API}/plants`).then(({ data }) => setPlants(data.plants || []))
      .catch((err) => toast.error("Could not load plants", { description: err?.response?.data?.detail || err.message }));
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/batches`);
      setHistory(data.batches || []);
    } catch (err) {
      toast.error("Could not load history", { description: err?.response?.data?.detail || err.message });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadMappings = useCallback(async () => {
    setMappingLoading(true);
    try {
      const { data } = await axios.get(`${API}/adg-mapping`);
      setMappings(data.mappings || []);
    } catch (err) {
      toast.error("Could not load category mappings", { description: err?.response?.data?.detail || err.message });
    } finally {
      setMappingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === "history") loadHistory();
    if (activeView === "mapping") loadMappings();
  }, [activeView, loadHistory, loadMappings]);

  const resetAll = () => {
    setUploadResult(null);
    setSelectedPlants([]);
    setValidFrom(DEFAULT_VALID_FROM);
    setBatch(null);
    setItems([]);
    setStatusFilter("ALL");
    setSearch("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload`, formData);
      setUploadResult(data);
      setBatch(null);
      setItems([]);
      if (data.items.length === 0) {
        toast.error("No usable rows found", { description: "Every row had a blank/invalid Item Code or Price - see the errors below." });
      } else {
        toast.success(`Parsed ${data.items.length} unique item(s)`, data.parse_errors.length ? { description: `${data.parse_errors.length} row(s) had an issue - see below.` } : undefined);
      }
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
      setUploadResult(null);
    } finally {
      setUploading(false);
    }
  };

  const togglePlant = (plantId) => {
    setSelectedPlants((prev) => (prev.includes(plantId) ? prev.filter((p) => p !== plantId) : [...prev, plantId]));
  };

  const pollValidateJob = async (jobId) => {
    for (;;) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const { data } = await axios.get(`${API}/validate/status/${jobId}`);
      if (data.status === "done") return data.result;
      if (data.status === "failed") throw new Error(data.error || "Validation failed");
    }
  };

  const loadItems = useCallback(async (batchId, status, searchTerm) => {
    setItemsLoading(true);
    try {
      const params = {};
      if (status && status !== "ALL") params.status = status;
      if (searchTerm) params.search = searchTerm;
      const { data } = await axios.get(`${API}/batches/${batchId}/items`, { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error("Could not load records", { description: err?.response?.data?.detail || err.message });
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const runValidation = async () => {
    if (!uploadResult?.items?.length) return;
    if (!uploadResult.has_per_item_plant && selectedPlants.length === 0) {
      toast.error("Select at least one plant");
      return;
    }
    const myToken = ++validateTokenRef.current;
    setValidating(true);
    setElapsedSeconds(0);
    const timer = setInterval(() => {
      if (validateTokenRef.current === myToken) setElapsedSeconds((s) => s + 1);
    }, 1000);
    try {
      const { data } = await axios.post(`${API}/validate`, {
        items: uploadResult.items,
        selected_plants: uploadResult.has_per_item_plant ? [] : selectedPlants,
        valid_from: validFrom,
        source_filename: uploadResult.source_filename,
        total_uploaded_items: uploadResult.total_rows,
      });
      const result = await pollValidateJob(data.job_id);
      if (validateTokenRef.current !== myToken) return;
      setBatch(result);
      setStatusFilter("ALL");
      setSearch("");
      await loadItems(result.batch_id, "ALL", "");
      toast.success(`Validated ${result.total_combinations} combination(s)`, {
        description: `${result.valid_records} ready, ${result.invalid_records} excluded.`,
      });
    } catch (err) {
      if (validateTokenRef.current === myToken) {
        toast.error("Validation failed", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      clearInterval(timer);
      if (validateTokenRef.current === myToken) setValidating(false);
    }
  };

  useEffect(() => {
    if (!batch) return;
    const handle = setTimeout(() => loadItems(batch.batch_id, statusFilter, search), 200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, batch]);

  const openBatchFromHistory = async (b) => {
    setActiveView("workflow");
    setItemsLoading(true);
    try {
      const { data } = await axios.get(`${API}/batches/${b.batch_id}`);
      setBatch(data);
      setUploadResult({ items: [], total_rows: data.total_uploaded_items, duplicate_count: 0, parse_errors: [], source_filename: data.source_filename, unique_materials: data.unique_materials });
      setSelectedPlants(data.selected_plants || []);
      setValidFrom(data.valid_from);
      setStatusFilter("ALL");
      setSearch("");
      await loadItems(data.batch_id, "ALL", "");
    } catch (err) {
      toast.error("Could not open this batch", { description: err?.response?.data?.detail || err.message });
    } finally {
      setItemsLoading(false);
    }
  };

  const startEditMapping = (m) => {
    setEditingCategory(m.product_category_id);
    setEditCode(m.code || "");
    setEditLabel(m.label || "");
  };

  const saveMapping = async (categoryId) => {
    if (!editCode.trim() || !editLabel.trim()) {
      toast.error("Code and Label are both required");
      return;
    }
    setSavingMapping(true);
    try {
      await axios.put(`${API}/adg-mapping/${categoryId}`, { code: editCode.trim(), label: editLabel.trim(), active: true });
      toast.success(`${categoryId} mapping saved`);
      setEditingCategory(null);
      await loadMappings();
    } catch (err) {
      toast.error("Could not save mapping", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSavingMapping(false);
    }
  };

  const canValidate = uploadResult?.items?.length > 0 && !validating
    && (uploadResult?.has_per_item_plant || selectedPlants.length > 0);
  const readyCount = batch?.valid_records || 0;

  const statusCounts = batch?.status_counts || {};
  const allStatuses = useMemo(() => Object.keys(statusCounts), [statusCounts]);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <Coins size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="material-valuation-title">
            Material Valuation Activation
          </h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            Upload Item Codes + Price, pick the plant(s) and a Valid From date, then download the official
            SAP Material Valuation XML for manual upload into SAP Business ByDesign.
          </p>
        </div>

        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="mb-4">
            <TabsTrigger value="workflow" data-testid="material-valuation-tab-workflow">Activation</TabsTrigger>
            <TabsTrigger value="history" data-testid="material-valuation-tab-history">
              <ClockCounterClockwise size={14} className="mr-1" />History
            </TabsTrigger>
            <TabsTrigger value="mapping" data-testid="material-valuation-tab-mapping">
              <Gear size={14} className="mr-1" />Category Mapping
            </TabsTrigger>
          </TabsList>

          <TabsContent value="workflow">
            {/* ---------------- Step 1: Upload ---------------- */}
            <div className="bg-white border border-[#D0D5DD] rounded-md p-4 mb-5">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-heading text-sm font-bold text-[#1D2939]">Step 1 - Upload Materials</h2>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={resetAll} data-testid="material-valuation-reset-button">
                  <X size={13} weight="bold" />Reset
                </Button>
              </div>
              <p className="text-xs text-[#667085] font-sans mb-3">
                Excel (.xlsx) with just two columns: Item Code and Price. Description, Category, Unit and Account
                Determination Group are fetched automatically from SAP in Step 3.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <a
                  href={`${API}/upload-template.xlsx`}
                  className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-bold text-[#004B87] border border-[#D0D5DD] rounded-sm hover:bg-[#F9FAFB] w-fit"
                  data-testid="material-valuation-template-link"
                >
                  <DownloadSimple size={14} weight="bold" />Download Template
                </a>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => handleUpload(e.target.files?.[0] || null)}
                  className="text-xs flex-1"
                  data-testid="material-valuation-file-input"
                />
                {uploading && <span className="text-xs text-[#667085]">Parsing...</span>}
              </div>

              {uploadResult && (
                <div className="mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    <StatCard label="Total Uploaded Rows" value={uploadResult.total_rows} testId="material-valuation-stat-uploaded" />
                    <StatCard label="Unique Items" value={uploadResult.items.length || uploadResult.unique_materials || 0} testId="material-valuation-stat-unique" />
                    <StatCard label="Duplicates Removed" value={uploadResult.duplicate_count} testId="material-valuation-stat-duplicates" />
                    <StatCard label="Row Errors" value={uploadResult.parse_errors.length} testId="material-valuation-stat-parse-errors" tone={uploadResult.parse_errors.length ? "bad" : undefined} />
                  </div>
                  {uploadResult.parse_errors.length > 0 && (
                    <div className="bg-[#FEF3F2] border border-[#FDA29B] rounded-md p-3 text-xs text-[#B42318] font-sans max-h-32 overflow-y-auto" data-testid="material-valuation-parse-errors">
                      {uploadResult.parse_errors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ---------------- Step 2: Plants + Date ---------------- */}
            {uploadResult?.items?.length > 0 && (
              <div className="bg-white border border-[#D0D5DD] rounded-md p-4 mb-5">
                <h2 className="font-heading text-sm font-bold text-[#1D2939] mb-3">Step 2 - Select Plants and Valid From Date</h2>
                <div className="flex flex-col sm:flex-row gap-6">
                  <div>
                    <div className="text-xs font-semibold text-[#475467] mb-2">Plants</div>
                    {uploadResult.has_per_item_plant ? (
                      <div className="text-sm text-[#1D2939] bg-[#ECFDF3] border border-[#ABEFC6] rounded-md px-3 py-2 max-w-sm" data-testid="material-valuation-per-item-plant-banner">
                        Your file has its own Site column - each material will activate at the plant listed next to it.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-4">
                        {plants.map((p) => (
                          <label key={p.plant_id} className="flex items-center gap-2 text-sm text-[#1D2939] cursor-pointer" data-testid={`material-valuation-plant-option-${p.plant_id}`}>
                            <Checkbox
                              checked={selectedPlants.includes(p.plant_id)}
                              onCheckedChange={() => togglePlant(p.plant_id)}
                              data-testid={`material-valuation-plant-checkbox-${p.plant_id}`}
                            />
                            {p.plant_id} <span className="text-[#98A2B3]">({p.company_id})</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-[#475467] mb-2">Valuation Valid From</div>
                    <Input
                      type="date"
                      value={validFrom}
                      onChange={(e) => setValidFrom(e.target.value)}
                      className="w-44 h-9"
                      data-testid="material-valuation-valid-from-input"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    className="gap-2 bg-[#0E7C86] hover:bg-[#0B6B74]"
                    disabled={!canValidate}
                    onClick={runValidation}
                    data-testid="material-valuation-validate-button"
                  >
                    <ArrowsClockwise size={15} weight="bold" className={validating ? "animate-spin" : ""} />
                    {validating ? `Validating... (${elapsedSeconds}s)` : "Fetch Details & Validate Records"}
                  </Button>
                  {uploadResult.has_per_item_plant ? (
                    <span className="text-xs text-[#667085] ml-3">{uploadResult.items.length} combination(s) (one plant per item)</span>
                  ) : selectedPlants.length > 0 && (
                    <span className="text-xs text-[#667085] ml-3">
                      {uploadResult.items.length} item(s) × {selectedPlants.length} plant(s) = up to {uploadResult.items.length * selectedPlants.length} combination(s)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ---------------- Step 3: Results ---------------- */}
            {batch && (
              <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <h2 className="font-heading text-sm font-bold text-[#1D2939]">Step 3 - Validation Results</h2>
                  <div className="flex gap-2">
                    <a
                      href={readyCount > 0 ? `${API}/batches/${batch.batch_id}/download-pending.xlsx` : undefined}
                      aria-disabled={readyCount === 0}
                      className={`inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold rounded-sm border ${readyCount > 0 ? "text-[#0E7C86] border-[#0E7C86] hover:bg-[#F0FDFA]" : "text-[#D0D5DD] border-[#D0D5DD] pointer-events-none"}`}
                      data-testid="material-valuation-download-pending-button"
                      title="Item Code / Plant / Price list of every Ready item - for activating valuation manually in SAP"
                    >
                      <DownloadSimple size={14} weight="bold" />Download Pending List
                    </a>
                    <a
                      href={`${API}/batches/${batch.batch_id}/download-errors.xlsx`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold text-[#B42318] border border-[#FDA29B] rounded-sm hover:bg-[#FEF3F2]"
                      data-testid="material-valuation-download-errors-button"
                    >
                      <DownloadSimple size={14} weight="bold" />Download Error Report
                    </a>
                    <a
                      href={readyCount > 0 ? `${API}/batches/${batch.batch_id}/download-xml` : undefined}
                      aria-disabled={readyCount === 0}
                      className={`inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold rounded-sm text-white ${readyCount > 0 ? "bg-[#0E7C86] hover:bg-[#0B6B74]" : "bg-[#D0D5DD] pointer-events-none"}`}
                      data-testid="material-valuation-download-xml-button"
                    >
                      <DownloadSimple size={14} weight="bold" />Download SAP XML ({readyCount})
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <StatCard label="Total Combinations" value={batch.total_combinations} testId="material-valuation-stat-combinations" />
                  <StatCard label="Validated / Ready" value={batch.valid_records} testId="material-valuation-stat-ready" />
                  <StatCard label="Rejected Records" value={batch.rejected_records} testId="material-valuation-stat-rejected" tone={batch.rejected_records ? "bad" : undefined} />
                  <StatCard label="Missing ADG Mapping" value={batch.missing_adg_mappings} testId="material-valuation-stat-missing-adg" tone={batch.missing_adg_mappings ? "bad" : undefined} />
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search item code"
                      className="pl-9 h-9"
                      data-testid="material-valuation-search-input"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-56 h-9" data-testid="material-valuation-status-filter-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Statuses</SelectItem>
                      {allStatuses.map((s) => (
                        <SelectItem key={s} value={s}>{s} ({statusCounts[s]})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-[#475467] ml-auto font-sans">{items.length.toLocaleString()} row(s)</span>
                </div>

                <div className="border border-[#EAECF0] rounded-md overflow-x-auto max-h-[480px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-[#EAECF0] text-left text-[#667085] font-sans">
                        <th className="px-3 py-2 font-medium">Item Code</th>
                        <th className="px-3 py-2 font-medium">Description</th>
                        <th className="px-3 py-2 font-medium">Plant</th>
                        <th className="px-3 py-2 font-medium">Company</th>
                        <th className="px-3 py-2 font-medium">Set of Books</th>
                        <th className="px-3 py-2 font-medium">Category</th>
                        <th className="px-3 py-2 font-medium">ADG</th>
                        <th className="px-3 py-2 font-medium text-right">Price</th>
                        <th className="px-3 py-2 font-medium">Unit</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsLoading && (
                        <tr><td colSpan={11} className="px-3 py-6 text-center text-[#667085]" data-testid="material-valuation-items-loading">Loading...</td></tr>
                      )}
                      {!itemsLoading && items.length === 0 && (
                        <tr><td colSpan={11} className="px-3 py-6 text-center text-[#667085]" data-testid="material-valuation-items-empty">No records match this filter.</td></tr>
                      )}
                      {items.map((it) => (
                        <tr key={it.id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`material-valuation-row-${it.id}`}>
                          <td className="px-3 py-2 font-mono font-semibold text-[#1D2939]">{it.material_id}</td>
                          <td className="px-3 py-2 text-[#475467] max-w-[180px] truncate" title={it.description || ""}>{it.description || "\u2014"}</td>
                          <td className="px-3 py-2 text-[#475467]">{it.plant_id}</td>
                          <td className="px-3 py-2 text-[#475467]">{it.company_id || "\u2014"}</td>
                          <td className="px-3 py-2 text-[#475467]">{it.set_of_books || "\u2014"}</td>
                          <td className="px-3 py-2 text-[#475467]">{it.product_category_id || "\u2014"}</td>
                          <td className="px-3 py-2 text-[#475467]">{it.account_determination_group || "\u2014"}</td>
                          <td className="px-3 py-2 text-right text-[#475467]">{it.price != null ? Number(it.price).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "\u2014"}</td>
                          <td className="px-3 py-2 text-[#475467]">{it.reference_qty_uom || it.base_unit || "\u2014"}</td>
                          <td className="px-3 py-2"><StatusBadge status={it.validation_status} /></td>
                          <td className="px-3 py-2 text-[#667085]">{(it.validation_messages || []).join("; ") || "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="font-heading text-sm font-bold text-[#1D2939] mb-3">Generation History</h2>
              <div className="border border-[#EAECF0] rounded-md overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#EAECF0] text-left text-[#667085] font-sans">
                      <th className="px-3 py-2 font-medium">Created</th>
                      <th className="px-3 py-2 font-medium">By</th>
                      <th className="px-3 py-2 font-medium">Plants</th>
                      <th className="px-3 py-2 font-medium">Valid From</th>
                      <th className="px-3 py-2 font-medium text-right">Ready / Total</th>
                      <th className="px-3 py-2 font-medium">XML Generated</th>
                      <th className="px-3 py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-[#667085]" data-testid="material-valuation-history-loading">Loading...</td></tr>
                    )}
                    {!historyLoading && history.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-[#667085]" data-testid="material-valuation-history-empty">No batches generated yet.</td></tr>
                    )}
                    {history.map((b) => (
                      <tr key={b.batch_id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`material-valuation-history-row-${b.batch_id}`}>
                        <td className="px-3 py-2 text-[#475467]">{new Date(b.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2 text-[#475467]">{b.uploaded_by_name || b.uploaded_by}</td>
                        <td className="px-3 py-2 text-[#475467]">{(b.selected_plants || []).join(", ")}</td>
                        <td className="px-3 py-2 text-[#475467]">{b.valid_from}</td>
                        <td className="px-3 py-2 text-right text-[#475467]">{b.valid_records} / {b.total_combinations}</td>
                        <td className="px-3 py-2">
                          {b.generation_status === "xml_generated"
                            ? <span className="text-[#027A48] flex items-center gap-1"><CheckCircle size={14} weight="fill" />{b.xml_record_count} record(s)</span>
                            : <span className="text-[#98A2B3]">Not yet</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" className="gap-1" onClick={() => openBatchFromHistory(b)} data-testid={`material-valuation-history-open-${b.batch_id}`}>
                            Open<ArrowRight size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="mapping">
            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="font-heading text-sm font-bold text-[#1D2939] mb-1">Product Category to Account Determination Group</h2>
              <p className="text-xs text-[#667085] font-sans mb-3">
                A category with no mapping here shows "ADG Mapping Missing" during validation and is excluded from
                the XML until you configure it - never guessed automatically.
              </p>
              <div className="border border-[#EAECF0] rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#EAECF0] text-left text-[#667085] font-sans">
                      <th className="px-3 py-2 font-medium">Product Category</th>
                      <th className="px-3 py-2 font-medium">ADG Code</th>
                      <th className="px-3 py-2 font-medium">ADG Label</th>
                      <th className="px-3 py-2 font-medium">Written to XML as</th>
                      <th className="px-3 py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappingLoading && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-[#667085]">Loading...</td></tr>
                    )}
                    {["RM", "SFG", "FG", "CONSUMABLES", "SCRAP"].map((categoryId) => {
                      const m = mappings.find((x) => x.product_category_id === categoryId);
                      const isEditing = editingCategory === categoryId;
                      return (
                        <tr key={categoryId} className="border-b border-[#F2F4F7]" data-testid={`material-valuation-mapping-row-${categoryId}`}>
                          <td className="px-3 py-2 font-semibold text-[#1D2939]">{categoryId}</td>
                          {isEditing ? (
                            <>
                              <td className="px-3 py-2"><Input value={editCode} onChange={(e) => setEditCode(e.target.value)} className="h-8 w-24" data-testid={`material-valuation-mapping-code-input-${categoryId}`} /></td>
                              <td className="px-3 py-2"><Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="h-8" data-testid={`material-valuation-mapping-label-input-${categoryId}`} /></td>
                              <td className="px-3 py-2 text-[#98A2B3]">{editCode} - {editLabel}</td>
                              <td className="px-3 py-2 text-right flex gap-2 justify-end">
                                <Button size="sm" className="bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={savingMapping} onClick={() => saveMapping(categoryId)} data-testid={`material-valuation-mapping-save-${categoryId}`}>Save</Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-3 py-2 text-[#475467]">{m?.code || "\u2014"}</td>
                              <td className="px-3 py-2 text-[#475467]">{m?.label || "\u2014"}</td>
                              <td className="px-3 py-2">
                                {m ? <span className="text-[#027A48] font-mono">{m.display_value}</span> : <span className="flex items-center gap-1 text-[#B54708]"><WarningCircle size={14} weight="fill" />ADG Mapping Missing</span>}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Button size="sm" variant="outline" onClick={() => startEditMapping(m || { product_category_id: categoryId, code: "", label: "" })} data-testid={`material-valuation-mapping-edit-${categoryId}`}>
                                  {m ? "Edit" : "Configure"}
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
